import React, { useRef, useState, useEffect } from "react";
import { AppTooltip } from "components/AppTooltip/AppTooltip";

interface SmartTruncatedTextProps {
    text?: string | null;
    width?: string | number;
    maxWidth?: string | number;
    /** Inherited by default, so the text has the cursor of the cell or link it sits in. */
    cursor?: "pointer" | "default" | "auto" | "inherit";
    className?: string;
    whiteSpace?: React.CSSProperties["whiteSpace"];
    placement?: "top" | "bottom" | "left" | "right";
    onClick?: () => void;
    id?: string;
    /** `span` where only phrasing content may go, e.g. inside a button. */
    as?: "div" | "span";
    /** Raises the tooltip above a layer that sits higher than MUI's own (a select's menu portal). */
    tooltipZIndex?: number;
}

/** Text that ends in "…" when it does not fit, and then shows the whole of it in the app's tooltip. */
const SmartTruncatedText: React.FC<SmartTruncatedTextProps> = ({
    text,
    width = "max-content",
    maxWidth = "100%",
    className = "",
    cursor = "inherit",
    whiteSpace = "nowrap",
    placement = "right",
    onClick,
    id,
    as: Element = "div",
    tooltipZIndex,
}) => {
    const textRef = useRef<HTMLElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        const measure = () => {
            if (el.clientWidth === 0) return;
            setIsTruncated(el.scrollWidth > el.clientWidth);
        };
        measure();

        // The element can also change size without a re-render (resizing a table column, for
        // example), so re-measure on layout changes or the tooltip goes stale.
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [text, width, maxWidth]);

    const textStyles: React.CSSProperties = {
        width: typeof width === "number" ? `${width}px` : width,
        maxWidth: typeof maxWidth === "number" ? `${maxWidth}px` : maxWidth,
        minWidth: 0,
        cursor,
        whiteSpace,
        display: "block",
    };

    const commonProps = {
        ref: textRef as React.RefObject<any>,
        id,
        className: `truncated-text break-title ${className}`,
        style: textStyles,
        onClick
    };

    const textElement = <Element {...commonProps}>{text}</Element>;

    // Always inside the tooltip, which is off while the text fits: switching between the two would
    // replace the element, and the ResizeObserver would go on watching the old one - a text cut off
    // once, before its column had its width, kept its tooltip for good.
    return (
        <AppTooltip title={text} placement={placement} zIndex={tooltipZIndex} disabled={!isTruncated}>
            {textElement}
        </AppTooltip>
    );
};

export default SmartTruncatedText;
